import React, { useState, useCallback, useEffect } from 'react';
import { Button, CardComponent, AlertComponent, InputComponent } from '../../../components/shared';
import { authActions, MFASetupData } from '../../../state/features/authReducer';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../state/features/reducers';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from '@reduxjs/toolkit';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

export interface MFASetupProps {
    onComplete: () => void;
    onCancel: () => void;
    username: string;
    accessToken: string;
    setupType: 'totp' | 'sms' | 'email';
}

export const MFASetup: React.FC<MFASetupProps> = ({
    onComplete,
    onCancel,
    username,
    accessToken,
    setupType
}) => {
    const [verificationCode, setVerificationCode] = useState<string>('');
    const [setupStep, setSetupStep] = useState<'setup' | 'verify'>('setup');

    const { t } = useTranslation();
    const errorMessage = useSelector((x: RootState) => x.auth.error);
    const authLoading = useSelector((x: RootState) => x?.auth?.loading);
    const mfaSetupData = useSelector((x: RootState) => x.auth.mfaSetupData);
    const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();

    // Initialize MFA setup when component mounts
    useEffect(() => {
        if (!mfaSetupData && setupStep === 'setup') {
            dispatch(authActions.setupTOTPMFA({ username, accessToken }));
        }
    }, [username, accessToken, mfaSetupData, setupStep, dispatch]);

    const onVerifySetup = useCallback(async () => {
        if (verificationCode.length === 6) {
            dispatch(authActions.verifyTOTPMFA({
                username,
                accessToken,
                code: verificationCode.trim()
            }));
            onComplete();
        } else {
            console.warn('Invalid verification code');
        }
    }, [verificationCode, username, accessToken, dispatch]);

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Enter' && setupStep === 'verify') {
            onVerifySetup();
        }
    }, [onVerifySetup, setupStep]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    const handleContinueToVerify = () => {
        setSetupStep('verify');
    };

    const handleBackToSetup = () => {
        setSetupStep('setup');
        setVerificationCode('');
    };

    const clickGate = Boolean(authLoading);

    if (setupStep === 'setup') {
        return (
            <CardComponent title={t('mfa.setupAuthenticatorApp')} className='login-card'>
                {errorMessage && <AlertComponent className='alertText' message={errorMessage} type='error' />}

                <div style={{ textAlign: 'center', marginBottom: '20px', color: 'white' }}>
                    <h3>{t('mfa.setupStep1')}</h3>
                    <p>{t('mfa.setupStep1Description')}</p>
                </div>

                {mfaSetupData?.SecretCode && (
                    <div style={{ textAlign: 'center', marginBottom: '20px', backgroundColor: 'white', padding: '20px', borderRadius: '8px' }}>
                        <QRCodeSVG
                            value={`otpauth://totp/${username}?secret=${mfaSetupData?.SecretCode}&issuer=${process.env.REACT_APP_AUTH_URL}`}
                            size={200}
                            level="M"
                        />
                    </div>
                )}

                {mfaSetupData?.SecretCode && (
                    <div style={{ marginBottom: '20px', textAlign: 'center', color: 'white' }}>
                        <p><strong>{t('mfa.manualEntryCode')}</strong></p>
                        <div style={{
                            backgroundColor: '#f0f0f0',
                            color: '#000',
                            padding: '10px',
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                            fontSize: '14px',
                            wordBreak: 'break-all'
                        }}>
                            {mfaSetupData.SecretCode}
                        </div>
                        <p style={{ fontSize: '12px', marginTop: '10px' }}>
                            {t('mfa.manualEntryDescription')}
                        </p>
                    </div>
                )}
                {mfaSetupData?.SecretCode && (
                    <div style={{ textAlign: 'center' }}>
                        <Button
                            type='primary'
                            onClickEvent={handleContinueToVerify}
                            disabled={clickGate || !mfaSetupData}
                            className='SignBtn'
                            style={{ borderLeft: '10px', borderRight: '10px' }}
                        >
                            {t('mfa.continueToVerification')}
                        </Button>
                    </div>
                )}
                <div style={{ textAlign: 'center', marginTop: '15px' }}>
                    <Button
                        type='link'
                        onClickEvent={onCancel}
                        className='forgetPassword'
                    >
                        {t('mfa.cancelSetup')}
                    </Button>
                </div>
            </CardComponent>
        );
    }

    return (
        <CardComponent title={t('mfa.setupAuthenticatorApp')} className='login-card'>
            {errorMessage && <AlertComponent className='alertText' message={errorMessage} type='error' />}

            <div style={{ textAlign: 'center', marginBottom: '20px', color: 'white' }}>
                <h3>{t('mfa.setupStep2')}</h3>
                <p>{t('mfa.setupStep2Description')}</p>
            </div>

            <div className='fieldView'>
                <label className='labelView'>{t('auth.verificationCode')}</label>
                <InputComponent
                    value={verificationCode}
                    placeholder={t('auth.enterCodeFromAuthenticator')}
                    onChangeEvent={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                        if (value.length <= 6) {
                            setVerificationCode(value);
                        }
                    }}
                    disabled={clickGate}
                    maxLength={6}
                />
            </div>

            <div className='signBtnView'>
                <Button
                    type='primary'
                    onClickEvent={onVerifySetup}
                    disabled={clickGate || verificationCode.length < 6}
                    className='SignBtn'
                >
                    {t('mfa.completeSetup')}
                </Button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '15px' }}>
                <Button
                    type='link'
                    onClickEvent={handleBackToSetup}
                    className='forgetPassword'
                >
                    {t('mfa.backToQRCode')}
                </Button>
            </div>

            <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <Button
                    type='link'
                    onClickEvent={onCancel}
                    className='forgetPassword'
                >
                    {t('mfa.cancelSetup')}
                </Button>
            </div>
        </CardComponent>
    );
};
